import AppButton from '@nxsys/components/AppButton';

import AppCardTabListContainer from '@nxsys/components/AppCardTabListContainer';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import {
  useUpdateBoostworksSettings,
  useUpdateSafeRecSettings,
  useUpdateVeriPayeSettings,
} from '@nxsys/hooks/api/company';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Icons } from '@nxsys/types/icons';
import { ICompany } from '@nxsys/types/models/company';
import { showMessage } from '@nxsys/utils/message';
import { useMemo, useRef, useState } from 'react';
import BoostworksSettingsForm, {
  BoostworksSettingsFormValues,
} from './components/BoostworksSettingsForm';
import SafeRecSettingsForm, {
  SafeRecSettingsFormValues,
} from './components/SafeRecSettingsForm';
import VeriPayeSettingsForm, {
  VeriPayeSettingsFormValues,
} from './components/VeriPayeSettingsForm';
import ZohoSettingsForm from './components/ZohoSettingsForm';

type Props = {
  data?: ICompany;
  companyId?: string;
  // Refetch after saving, or a sub-tab remount re-seeds its form from stale data.
  fetchCompany?: () => void;
};

enum IntegrationTabEnum {
  ZOHO_SETTINGS = 'ZOHO_SETTINGS',
  SAFE_REC_SETTINGS = 'SAFE_REC_SETTINGS',
  BOOST_WORKS_SETTINGS = 'BOOST_WORKS_SETTINGS',
  VERI_PAYE_SETTINGS = 'VERI_PAYE_SETTINGS',
}

const IntegrationTab: React.FC<Props> = ({ data, companyId, fetchCompany }) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;

  const tabs = useMemo(
    () => [
      {
        tab: 'Zoho Settings',
        key: IntegrationTabEnum.ZOHO_SETTINGS,
      },
      // NX-5228: SafeRec and VeriPAYE are UK payroll-compliance services;
      // neither operates on Irish payrolls.
      ...(isIreland
        ? []
        : [
            {
              tab: 'SafeRec Settings',
              key: IntegrationTabEnum.SAFE_REC_SETTINGS,
            },
          ]),
      {
        tab: 'Boostworks Settings',
        key: IntegrationTabEnum.BOOST_WORKS_SETTINGS,
      },
      ...(isIreland
        ? []
        : [
            {
              tab: 'VeriPaye Settings',
              key: IntegrationTabEnum.VERI_PAYE_SETTINGS,
            },
          ]),
    ],
    [isIreland],
  );

  const [activeTab, setActiveTab] = useState<IntegrationTabEnum>(
    IntegrationTabEnum.ZOHO_SETTINGS,
  );
  const safeRecSettingsFormRef = useRef<{ submit: () => void } | null>(null);
  const boostworksSettingsFormRef = useRef<{ submit: () => void } | null>(null);
  const veriPayeSettingsFormRef = useRef<{ submit: () => void } | null>(null);

  const handleTabChange = (key: IntegrationTabEnum) => {
    setActiveTab(key);
  };

  const handleSave = () => {
    if (activeTab === IntegrationTabEnum.SAFE_REC_SETTINGS) {
      safeRecSettingsFormRef.current?.submit();
    } else if (activeTab === IntegrationTabEnum.BOOST_WORKS_SETTINGS) {
      boostworksSettingsFormRef.current?.submit();
    } else if (activeTab === IntegrationTabEnum.VERI_PAYE_SETTINGS) {
      veriPayeSettingsFormRef.current?.submit();
    }
  };

  // Zoho settings handled inside ZohoSettingsForm

  const { run: updateSafeRecSettings, loading: isUpdatingSafeRecSettings } =
    useUpdateSafeRecSettings();
  const handleSafeRecSettingsSubmit = async (
    values: SafeRecSettingsFormValues,
  ) => {
    if (!companyId || !data) return;

    await updateSafeRecSettings(companyId, {
      integratedCompanyId: values.integratedCompanyId || '',
    });
    showMessage({
      type: 'success',
      content: messages['common.updateSuccess'],
    });
    fetchCompany?.();
  };

  const {
    run: updateBoostworksSettings,
    loading: isUpdatingBoostworksSettings,
  } = useUpdateBoostworksSettings();
  const handleBoostworksSettingsSubmit = async (
    values: BoostworksSettingsFormValues,
  ) => {
    if (!companyId || !data) return;

    await updateBoostworksSettings(companyId, {
      clientId: values.clientId || '',
      ivKey: values.ivKey || '',
      symmetricKey: values.symmetricKey || '',
      clientKey: values.clientKey || '',
      secretKey: values.secretKey || '',
    });
    showMessage({
      type: 'success',
      content: messages['common.updateSuccess'],
    });
    fetchCompany?.();
  };

  const { run: updateVeriPayeSettings, loading: isUpdatingVeriPayeSettings } =
    useUpdateVeriPayeSettings();
  const handleVeriPayeSettingsSubmit = async (
    values: VeriPayeSettingsFormValues,
  ) => {
    if (!companyId || !data) return;

    await updateVeriPayeSettings(companyId, {
      clientId: values.clientId || '',
      accessKey: values.accessKey || '',
      secretKey: values.secretKey || '',
      bucketName: values.bucketName || '',
    });
    showMessage({
      type: 'success',
      content: messages['common.updateSuccess'],
    });
    fetchCompany?.();
  };

  const isUpdating =
    isUpdatingSafeRecSettings ||
    isUpdatingBoostworksSettings ||
    isUpdatingVeriPayeSettings;

  const showSave = activeTab !== IntegrationTabEnum.ZOHO_SETTINGS;

  const renderTabContent = () => {
    switch (activeTab) {
      case IntegrationTabEnum.ZOHO_SETTINGS:
        return <ZohoSettingsForm companyData={data} />;
      case IntegrationTabEnum.SAFE_REC_SETTINGS:
        return (
          <SafeRecSettingsForm
            companyData={data}
            formRef={safeRecSettingsFormRef}
            onSubmit={handleSafeRecSettingsSubmit}
          />
        );
      case IntegrationTabEnum.BOOST_WORKS_SETTINGS:
        return (
          <BoostworksSettingsForm
            companyData={data}
            formRef={boostworksSettingsFormRef}
            onSubmit={handleBoostworksSettingsSubmit}
          />
        );
      case IntegrationTabEnum.VERI_PAYE_SETTINGS:
        return (
          <VeriPayeSettingsForm
            companyData={data}
            formRef={veriPayeSettingsFormRef}
            onSubmit={handleVeriPayeSettingsSubmit}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <AppCardTabListContainer
      tabList={tabs}
      activeTab={activeTab}
      handleTabChange={handleTabChange}
      className="!border-none"
    >
      <div className="pt-[16px]">{renderTabContent()}</div>
      {data && showSave && (
        <div className="flex items-center justify-start gap-3 pt-4">
          <AppButton
            type="primary"
            name={messages['common.save']}
            leftIcon={
              <Icons.IconCheckCircle color={defaultTheme.theme.palette.white} />
            }
            onClick={handleSave}
            loading={isUpdating}
          />
        </div>
      )}
    </AppCardTabListContainer>
  );
};

export default IntegrationTab;
